/**
 * Offline / Online Banner for LOCALE Web.
 *
 * - Shows a red bar when the user loses internet.
 * - Briefly shows a green "Back online" bar when connectivity is restored.
 * - Zero cost during normal browsing (renders nothing when online).
 */

import { useEffect, useRef, useState } from "react";
import { WifiOff, Wifi } from "lucide-react";

export function OfflineBanner() {
  const [isOnline,       setIsOnline]       = useState(
    typeof navigator !== "undefined" ? navigator.onLine : true
  );
  const [showReconnected, setShowReconnected] = useState(false);
  const reconnectedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasGoneOffline   = useRef(false);

  useEffect(() => {
    function handleOnline() {
      setIsOnline(true);
      if (hasGoneOffline.current) {
        setShowReconnected(true);
        reconnectedTimer.current = setTimeout(() => {
          setShowReconnected(false);
        }, 3_000);
      }
      hasGoneOffline.current = false;
    }

    function handleOffline() {
      setIsOnline(false);
      hasGoneOffline.current = true;
      if (reconnectedTimer.current) {
        clearTimeout(reconnectedTimer.current);
        setShowReconnected(false);
      }
    }

    window.addEventListener("online",  handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online",  handleOnline);
      window.removeEventListener("offline", handleOffline);
      if (reconnectedTimer.current) clearTimeout(reconnectedTimer.current);
    };
  }, []);

  // Nothing to show during normal online operation
  if (isOnline && !showReconnected) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className={[
        "fixed top-0 left-0 right-0 z-[9999]",
        "flex items-center justify-center gap-2",
        "py-2 px-4 text-sm font-semibold text-white",
        "transition-colors duration-300",
        isOnline ? "bg-green-600" : "bg-red-600",
      ].join(" ")}
    >
      {isOnline ? (
        <>
          <Wifi   className="w-4 h-4 shrink-0" aria-hidden />
          <span>Back online</span>
        </>
      ) : (
        <>
          <WifiOff className="w-4 h-4 shrink-0" aria-hidden />
          <span>You're offline — some features may be unavailable</span>
        </>
      )}
    </div>
  );
}
